export const quickFilesContentRouteTemplate = `/**
 * Vibeship DevTools - File Content API Route
 * Endpoint: /api/vibeship/files/content
 */

import { NextRequest, NextResponse } from 'next/server';
import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'fs';
import { join, dirname } from 'path';

export async function GET(request: NextRequest) {
  try {
    const url = new URL(request.url);
    const path = url.searchParams.get('path');
    
    if (!path) {
      return NextResponse.json(
        { error: 'Path parameter required' },
        { status: 400 }
      );
    }
    
    // Simple path validation
    if (path.includes('..') || path.startsWith('/')) {
      return NextResponse.json(
        { error: 'Invalid path' },
        { status: 400 }
      );
    }
    
    const content = await readFile(path);
    return NextResponse.json({ content, path });
  } catch (error) {
    console.error('File read error:', error);
    return NextResponse.json(
      { 
        error: 'Failed to read file',
        message: error instanceof Error ? error.message : 'Unknown error'
      },
      { status: 500 }
    );
  }
}

export async function POST(request: NextRequest) {
  try {
    // Check if writes are disabled
    if (process.env.VIBESHIP_DISABLE_FILE_WRITES === 'true') {
      return NextResponse.json(
        { error: 'File writes are disabled' },
        { status: 403 }
      );
    }
    
    const { path, content } = await request.json();
    
    if (!path || content === undefined) {
      return NextResponse.json(
        { error: 'Path and content required' },
        { status: 400 }
      );
    }
    
    // Simple path validation
    if (path.includes('..') || path.startsWith('/')) {
      return NextResponse.json(
        { error: 'Invalid path' },
        { status: 400 }
      );
    }
    
    await writeFile(path, content);
    return NextResponse.json({ success: true, path });
  } catch (error) {
    console.error('File write error:', error);
    return NextResponse.json(
      { 
        error: 'Failed to write file',
        message: error instanceof Error ? error.message : 'Unknown error'
      },
      { status: 500 }
    );
  }
}

async function readFile(filePath: string): Promise<string> {
  // Try multiple path resolutions
  const pathsToTry = [
    join(process.cwd(), filePath), // Direct path
    join(process.cwd(), 'tickets', filePath), // With tickets prefix
    join(process.cwd(), 'docs', filePath), // With docs prefix
  ];
  
  for (const fullPath of pathsToTry) {
    if (existsSync(fullPath)) {
      return readFileSync(fullPath, 'utf8');
    }
  }
  
  // If not found, also check if the path already includes the scan directory
  if (!filePath.startsWith('tickets/') && !filePath.startsWith('docs/')) {
    const withPrefix = join(process.cwd(), 'tickets', filePath);
    if (existsSync(withPrefix)) {
      return readFileSync(withPrefix, 'utf8');
    }
  }
  
  throw new Error(\`File not found: \${filePath}\`);
}

async function writeFile(filePath: string, content: string): Promise<void> {
  // Try to determine the correct path
  let fullPath = join(process.cwd(), filePath);
  
  // If the file doesn't start with a known directory, try to find where it should go
  if (!filePath.startsWith('tickets/') && !filePath.startsWith('docs/')) {
    // Check if the file exists with tickets prefix
    const ticketsPath = join(process.cwd(), 'tickets', filePath);
    const docsPath = join(process.cwd(), 'docs', filePath);
    
    if (existsSync(ticketsPath)) {
      fullPath = ticketsPath;
    } else if (existsSync(docsPath)) {
      fullPath = docsPath;
    } else {
      // Default to tickets directory for new files
      fullPath = ticketsPath;
    }
  }
  
  // Ensure directory exists
  mkdirSync(dirname(fullPath), { recursive: true });
  
  writeFileSync(fullPath, content, 'utf8');
}
`;